import Vue from 'vue'
import firebase from "firebase/app";
import i18n from "@/i18n";
import brand from "@/config/brand";
import { ls } from "@/utils/storage";

// Lightweight replacement for moment.js: uses the platform's Intl.DateTimeFormat
// keyed off the current vue-i18n locale. ~60 KB removed from the bundle.
function _toDate(d) {
  if (d == null || d === "") return null;
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? null : parsed;
}
function _locale() {
  return (i18n && i18n.locale) || 'fr';
}

Vue.mixin({
    methods: {
        isEmptyOrNull(text) {
            return (text == null || text.toString().trim() === "")
        },
        getAdjustedTitle(currentTitle) {
            if (currentTitle == null) {
                return this.$t('common.not-available')
            }
            const trimmedCurrentTitle = currentTitle.trim()
            if (trimmedCurrentTitle === "") {
                return this.$t('common.not-available')
            }
            return trimmedCurrentTitle
        },
        truncateColumn(text, len = 50) {
            return text.substring(0, len) + (text.length > len ? " ..." : "");
        },
        toFixedIfNecessary(value, dp = 2){
            return +parseFloat(value).toFixed( dp );
        },
        getFullName: function (name) {
            return name.first + " " + name.last
        },
        // Switch the UI language and persist the choice (per-client namespaced),
        // so it survives reloads. Available to every user.
        changeLanguage(lang) {
            if (!lang || !i18n.messages || !i18n.messages[lang]) return;
            i18n.locale = lang;
            ls.set('locale', lang);
        },
        getFormattedQuantity: function (estimation, unit) {
            if (estimation == null || estimation === "" || estimation === undefined) {
                return ""
            }
            if (estimation.quantity == null || estimation.quantity === "" || estimation.quantity === undefined) {
                return ""
            }
            let u = this.quantityUnits.find(obj => {
                return obj.id === unit
            });
            return this.toFixedIfNecessary(estimation.quantity) + " " + (unit ? (u ? this.$t(u.translationAbbrKey) : unit) : "")
        },
        getFormattedQuantityEquipment: function (estimation) {
            if (estimation == null || estimation === "" || estimation === undefined) {
                return ""
            }
            if (estimation.quantity == null || estimation.quantity === "" || estimation.quantity === undefined) {
                return ""
            }
            let u = this.quantityUnits.find(obj => {
                return obj.id === this.defaultValues.equipmentQuantityUnitId
            });
            return this.toFixedIfNecessary(estimation.quantity) + " " + this.$t(u.translationAbbrKey)
        },
        getFormattedQuantityValue: function (quantity, unit) {
            if (quantity == null || quantity === "" || quantity === undefined) {
                return ""
            }
            let u = this.quantityUnits.find(obj => {
                return obj.id === unit
            });
            return this.toFixedIfNecessary(quantity) + " " + (unit ? (u ? this.$t(u.translationAbbrKey) : unit) : "")
        },
        getFormattedFeePerUnit: function (estimation) {
            if (estimation == null || estimation === "" || estimation === undefined) {
                return ""
            }
            return this.getFormattedPricing(estimation.fee_per_unit);
        },
        getFormattedPricing: function (value) {
            if (value == null || value === "" || value === undefined) {
                return ""
            }
            return parseFloat(value).toFixed(2) + " " + this.defaultValues.priceUnit
        },
        getFormattedPricePerDuration: function (value, unit) {
            if (value == null || value === "" || value === undefined) {
                return ""
            }
            let u = this.durationUnits.find(obj => {
                return obj.id === unit
            });
            return parseFloat(value).toFixed(2) + " " + this.defaultValues.priceUnit + "/" + (u ? this.$t(u.translationAbbrKey) : unit)
        },
        getFormattedPerformance: function (value, unit) {
            if (value == null || value === "" || value === undefined) {
                return ""
            }
            let u = this.quantityUnits.find(obj => {
                return obj.id === unit
            });
            return this.toFixedIfNecessary(value) + " " + (unit ? (u ? this.$t(u.translationAbbrKey) : unit) + "/" + this.$t("enums.duration-unit-short.day") : "")
        },
        getPerformanceUnit: function (unit, getAbbr = true) {
            return unit ? this.getTranslatedQuantityUnit(unit, getAbbr) + "/" + this.$t("enums.duration-unit-short.day") : ""
        },
        getTranslatedQuantityUnit: function (unit, getAbbr = true) {
            let u = this.quantityUnits.find(obj => {
                return obj.id === unit
            });
            return unit ? (u ? this.$t(getAbbr ? u.translationAbbrKey : u.translationKey) : unit) : ""
        },
        getFormattedTime: function (quantity) {
            if (quantity == null || quantity === "" || quantity === undefined) {
                return ""
            }
            const fixedPart = Math.floor(quantity)
            const decimalPart = (quantity % 1).toFixed(2) * 60
            return fixedPart + ":" + (decimalPart === 0 ? "00" : Math.round(decimalPart))
        },
        getFormattedTimeHour: function (quantity) {
            return this.getFormattedTime(quantity) + ' ' + this.$t("enums.duration-unit-short.hour")
        },
        profitTextStyle: function (profit) {
            return {
                color: profit >= 0 ? 'green' : 'red',
                'font-weight': 'bold'
            };
        },
        personalQuantityTextStyle: function (group) {
            if (group === 'internal') {
                return 'internal-text'
            } else {
                return 'external-text'
            }
        },
        isProjectSummaryHoursValid: function (groupedLog) {
            if (groupedLog === null || groupedLog === "" || groupedLog === undefined) {
                return true
            }
            return (groupedLog.total_logged_durations_internal === groupedLog.total_logged_durations_internal_personnel) && (groupedLog.total_logged_durations_external === groupedLog.total_logged_durations_external_personnel)
        },
        isHavingValue(groupedLog) {
            return (groupedLog.total_logged_durations_external !== "" && groupedLog.total_logged_durations_external !== undefined && groupedLog.total_logged_durations_external !== 0) ||
                (groupedLog.total_logged_durations_internal !== "" && groupedLog.total_logged_durations_internal !== undefined && groupedLog.total_logged_durations_internal !== 0) ||
                (groupedLog.total_logged_durations_external_personnel !== "" && groupedLog.total_logged_durations_external_personnel !== undefined && groupedLog.total_logged_durations_external_personnel !== 0) ||
                (groupedLog.total_logged_durations_internal_personnel !== "" && groupedLog.total_logged_durations_internal_personnel !== undefined && groupedLog.total_logged_durations_internal_personnel !== 0)
        },
        getFormattedDate: function (dateString) {
            // moment('L') equivalent — locale-aware short date (e.g. 14/11/2026 in fr).
            const d = _toDate(dateString);
            if (!d) return "";
            return new Intl.DateTimeFormat(_locale(), { dateStyle: 'short' }).format(d);
        },
        getFormattedDateTime: function (dateString) {
            // moment('LLLL') equivalent — full date with weekday + time.
            const d = _toDate(dateString);
            if (!d) return "";
            return new Intl.DateTimeFormat(_locale(), {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            }).format(d);
        },
        getUserRoleList: function() {
            let userRolesList = [];
            this.userRoleOptions.forEach(element => {
                if(element.id !== "system" || this.currentUserSystemAccessLevel()){
                  userRolesList.push(element);
                }
              });
            return userRolesList;
        },
        hasCurrentUserAccessToUserRole(userRole) {
            return this.currentUserSystemAccessLevel() 
                    || (this.currentUserAdminAccessLevel() && userRole !== "system");
        },
        isCurrentUser(userid) {
            return this.getLocalUserId() === userid;
        },
        getFirebaseStorageError: function (errorCode) {
            switch (errorCode) {
                case 'storage/unauthorized':
                    return this.$t('errors.storage-unauthorized');
                case 'storage/canceled':
                    return this.$t('errors.storage-canceled');
                case 'storage/unknown':
                    return this.$t('errors.storage-unknown');
                case 'storage/invalid-argument':
                    return this.$t('errors.storage-invalid-argument');
                case 'storage/invalid-checksum':
                    return this.$t('errors.storage-invalid-checksum');
                case 'storage/retry-limit-exceeded':
                    return this.$t('errors.storage-retry-limit-exceeded');
                default:
                    return this.$t('errors.storage-default');
            }
        },
        loadUserRoleFromServer,
        getLocalUserRole,
        getLocalUserId,
        getLocalUserSites,
        getLocalUserProjects,
        currentUserEngagedInProject,
        currentUserSystemAccessLevel,
        currentUserAdminAccessLevel,
        currentUserPMAccessLevel,
        currentUserSMAccessLevel,
        currentUserAccess,
        removeUserRole,
        getSubmitEndpoint,
        saveSubmitEndpoint,
        getStorageRootFolder
    }
})

// Per-user, env-scoped session cache (role / id / assigned sites + projects).
// The SAME Firebase project can host multiple databases behind different submit
// endpoints (e.g. submit -> prod, submitFr -> demo-fr), so this state MUST be
// keyed per endpoint or the two datasets clobber each other's role/assignments
// in shared localStorage. Held in a reactive store so access checks re-render
// when the role finishes (re)loading after a DB switch + reload.
const _userStore = Vue.observable({ role: null, id: null, sites: [], projects: [] });

function _epKey(base) {
    return getSubmitEndpoint() + ":" + base;
}
function _parseList(value) {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return String(value).split(",").map((s) => s.trim()).filter(Boolean);
}
function _hydrateUserStore() {
    _userStore.role = ls.get(_epKey('userRole')) || null;
    _userStore.id = ls.get(_epKey('userId')) || null;
    _userStore.sites = _parseList(ls.get(_epKey('userSites')));
    _userStore.projects = _parseList(ls.get(_epKey('userProjects')));
}
_hydrateUserStore();

// Resolve once Firebase has restored the auth state. On a full page reload
// (e.g. after switching database) currentUser is briefly null until restoration
// completes; reading it synchronously makes loadUserRoleFromServer bail and the
// role stay null — which then misfires access gates. Awaiting this avoids that.
function _authReady() {
    const auth = firebase.auth();
    if (auth.currentUser) return Promise.resolve(auth.currentUser);
    return new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
        });
    });
}

export async function loadUserRoleFromServer() {
    const currentUser = await _authReady();
    if (currentUser === undefined || currentUser === null) {
        return null
    }
    const isEmailVerified = currentUser.emailVerified
    if (!isEmailVerified) {
        return null
    }
    const functions = firebase.app().functions(this.$brand.functionsRegion);
    const getUserFunction = functions.httpsCallable(getSubmitEndpoint());
    return await getUserFunction({action: "get_users", filters: [['email', '==', currentUser.email]]})
        .then((result) => {
            const usersData = result.data["users"];
            const currentLoadedUser = usersData[0]
            const role = currentLoadedUser.roles[0]
            const sites = _parseList(currentLoadedUser.site_ids)
            const projects = _parseList(currentLoadedUser.project_ids)
            // Persist under the ACTIVE endpoint so the English/French datasets
            // (same Firebase project) keep separate role/assignment caches.
            ls.set(_epKey('userRole'), role)
            ls.set(_epKey('userId'), currentLoadedUser.document_id)
            ls.set(_epKey('userSites'), sites)
            ls.set(_epKey('userProjects'), projects)
            // Update the reactive store so access-gated UI re-renders immediately.
            _userStore.role = role
            _userStore.id = currentLoadedUser.document_id
            _userStore.sites = sites
            _userStore.projects = projects
            return role
        })
        .catch((error) => {
            if (error.message === "InactiveUser") {
                return "inactive"
            }
        })
}

export function getLocalUserRole() {
    return _userStore.role || null;
}

export function getLocalUserId() {
    return _userStore.id || null;
}

export function getLocalUserSites() {
    return _userStore.sites;
}

export function getLocalUserProjects() {
    return _userStore.projects;
}

export function removeUserRole() {
    // clear the active endpoint's cache (keys computed before dropping submit-endpoint)
    ls.remove(_epKey('userRole'));
    ls.remove(_epKey('userId'));
    ls.remove(_epKey('userSites'));
    ls.remove(_epKey('userProjects'));
    ls.remove('submit-endpoint');
    _userStore.role = null;
    _userStore.id = null;
    _userStore.sites = [];
    _userStore.projects = [];
}

/**
 * The next 4 functions returns the access level of the user not his role.
 * System > Admin > Business/Project Manager > Site Manager
 */
export function currentUserSystemAccessLevel() {
    return (this.getLocalUserRole() === "system");
}

export function currentUserAdminAccessLevel() {
    return (this.getLocalUserRole() === "admin" || this.currentUserSystemAccessLevel());
}

export function currentUserPMAccessLevel() {
    return (this.getLocalUserRole() === "project_manager" || this.currentUserAdminAccessLevel());
}

export function currentUserSMAccessLevel() {
    return (this.getLocalUserRole() === "site_manager" || this.currentUserPMAccessLevel());
}

export function currentUserEngagedInProject(projectId) {
    return this.getLocalUserSites().includes(projectId) || this.getLocalUserProjects().includes(projectId);
}

/**
 * Current user access to different pages
 */
export function currentUserAccess(page, action, projectId, userId) {
    if (page === "Users") {
        return this.currentUserAdminAccessLevel();
    }
    if (page === "UserDetails") {
        return this.getLocalUserId() === userId || this.currentUserAdminAccessLevel();
    }
    if (page === "Projects") {
        if (action === this.UserActions.VIEW)
            return this.currentUserSMAccessLevel();
        if (action === this.UserActions.CREATE)
            return this.currentUserPMAccessLevel();
        if (action === this.UserActions.UPDATE)
            return ((this.currentUserPMAccessLevel() && this.currentUserEngagedInProject(projectId))
                || this.currentUserAdminAccessLevel());
        if (action === this.UserActions.DELETE)
            return this.currentUserAdminAccessLevel();
    }
    if (page === "ProjectDetails") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
        if (action === this.UserActions.CREATE)
            return this.currentUserPMAccessLevel();
        if (action === this.UserActions.UPDATE || action === this.UserActions.DELETE)
            return ((this.currentUserPMAccessLevel() && this.currentUserEngagedInProject(projectId))
                || this.currentUserAdminAccessLevel());
    }
    if (page === "ProjectTemplates") {
        return this.currentUserPMAccessLevel();
    }
    if (page === "ProjectLogs") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
        if (action === this.UserActions.CREATE || action === this.UserActions.UPDATE || action === this.UserActions.DELETE)
            return this.currentUserAdminAccessLevel() || this.currentUserEngagedInProject(projectId);
    }
    if (page === "ProjectSummary") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
    }
    if (page === "TaskDetails") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
        if (action === this.UserActions.CREATE || action === this.UserActions.UPDATE || action === this.UserActions.DELETE)
            return ((this.currentUserPMAccessLevel() && (this.currentUserEngagedInProject(projectId)))
                || this.currentUserAdminAccessLevel());
    }
    if (page === "PersonalDetails") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
        if (action === this.UserActions.CREATE || action === this.UserActions.UPDATE || action === this.UserActions.DELETE)
            return this.currentUserAdminAccessLevel() || this.currentUserEngagedInProject(projectId);
    }
    if (page === "EquipmentDetails") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
        if (action === this.UserActions.CREATE || action === this.UserActions.UPDATE || action === this.UserActions.DELETE)
            return this.currentUserAdminAccessLevel() || this.currentUserEngagedInProject(projectId);
    }
    if (page === "ConsumableDetails") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
        if (action === this.UserActions.CREATE || action === this.UserActions.UPDATE || action === this.UserActions.DELETE)
            return this.currentUserAdminAccessLevel() || this.currentUserEngagedInProject(projectId);
    }
    if (page === "ProjectFilesDetails") {
        if (action === this.UserActions.VIEW)
            return this.currentUserPMAccessLevel() || this.currentUserEngagedInProject(projectId);
        if (action === this.UserActions.CREATE || action === this.UserActions.UPDATE || action === this.UserActions.DELETE)
            return this.currentUserAdminAccessLevel() || this.currentUserEngagedInProject(projectId);
    }
    if (page === "CategoricalData") {
            return this.currentUserSystemAccessLevel() || this.currentUserAdminAccessLevel();
    }

    return false;
}

/**
 * Other mixin functions
 */
export function getSubmitEndpoint() {
    const savedSelectedSubmit = ls.get("submit-endpoint")
    if (savedSelectedSubmit == null || savedSelectedSubmit === "" || savedSelectedSubmit === undefined) {
        return brand.defaultEndpoint;
    } else {
        return savedSelectedSubmit;
    }
}

export function saveSubmitEndpoint(selectedEndpoint) {
    ls.set("submit-endpoint", selectedEndpoint)
}

export function getStorageRootFolder() {
    // Storage path prefix — must match the server ENVIRONMENT of the ACTIVE
    // endpoint, so images live alongside the dataset the switcher selected.
    return brand.endpointStorageEnvs[getSubmitEndpoint()] || brand.storageEnv;
}